/**
 * IncomingDataButton Component
 *
 * This component provides a button in the MenuBar that displays incoming notes and photos
 * from the FocusMap Mobile companion app. It shows a badge with the count of new items
 * and opens a dialog where users can review and add these items to their map.
 *
 * Features:
 * - Displays a badge count of new notes and photos
 * - Polls Firebase every 30 seconds for new data
 * - Shows notes and photos in an organized dialog
 * - Creates new nodes on the map when "Add" is clicked
 * - Marks items as picked up in Firebase with timestamp
 *
 * Firebase Collections:
 * - notes: { id, content, flowId, userEmail, createdAt, isPickedUp }
 * - photos: { id, imageUrl, flowId, userEmail, createdAt, isPickedUp }
 *
 * Node Creation:
 * - Notes: Label is truncated to 25 chars (+ ellipsis), full text in notes property
 * - Photos: Label is "mobile photo", imageUrl stored in icon property
 */

import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Badge } from "primereact/badge";
import { ToggleButton } from "primereact/togglebutton";
import { db, auth } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { nanoid } from "nanoid";
import { useStore } from "../App/store";
import { Node } from "reactflow";
import { NodeData } from "../types/types";
import { getTranscript } from "../Helpers/AzureVision";
import "./IncomingDataButton.css";

interface IncomingNote {
  id: string;
  content: string;
  flowId: string;
  createdAt: any;
  isPickedUp?: any;
}

interface IncomingPhoto {
  id: string;
  imageUrl: string;
  flowId: string;
  createdAt: any;
  isPickedUp?: any;
}

interface IncomingDataButtonProps {
  flowId: string | undefined;
  onCountChange?: (count: number) => void;
  externalVisible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
}

export const IncomingDataButton: React.FC<IncomingDataButtonProps> = ({
  flowId,
  onCountChange,
  externalVisible,
  onVisibleChange,
}) => {
  const [internalVisible, setInternalVisible] = useState(false);
  const visible =
    externalVisible !== undefined ? externalVisible : internalVisible;
  const setVisible = onVisibleChange || setInternalVisible;

  const [notes, setNotes] = useState<IncomingNote[]>([]);
  const [photos, setPhotos] = useState<IncomingPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingPhotoId, setProcessingPhotoId] = useState<string | null>(
    null
  );
  const [totalCount, setTotalCount] = useState(0);

  // Notify parent of count changes
  useEffect(() => {
    if (onCountChange) {
      onCountChange(totalCount);
    }
  }, [totalCount, onCountChange]);

  const nodes = useStore((state) => state.nodes);
  const setNodes = useStore((state) => state.setNodes);
  const edges = useStore((state) => state.edges);
  const setEdges = useStore((state) => state.setEdges);
  const setIsDirty = useStore((state) => state.setIsDirty);

  // Fetch notes and photos when dialog opens
  useEffect(() => {
    if (visible && flowId) {
      fetchIncomingData();
    }
  }, [visible, flowId]);

  // Check for new data periodically to update badge
  useEffect(() => {
    if (flowId) {
      checkForNewData();
      const interval = setInterval(checkForNewData, 30000); // Check every 30 seconds
      return () => clearInterval(interval);
    }
  }, [flowId]);

  const checkForNewData = async () => {
    if (!flowId || !auth.currentUser) return;

    try {
      // console.log(
      //   "IncomingDataButton: Checking for new data - flowId:",
      //   flowId,
      //   "userEmail:",
      //   auth.currentUser.email
      // );
      // Query for notes and photos belonging to this flow and user
      // We can't directly query for missing fields in Firestore, so we filter after
      const notesQuery = query(
        collection(db, "notes"),
        where("flowId", "==", flowId),
        where("userEmail", "==", auth.currentUser.email)
      );

      const photosQuery = query(
        collection(db, "photos"),
        where("flowId", "==", flowId),
        where("userEmail", "==", auth.currentUser.email)
      );

      const [notesSnapshot, photosSnapshot] = await Promise.all([
        getDocs(notesQuery),
        getDocs(photosQuery),
      ]);

      // Filter out items that have already been picked up
      const unpickedNotesCount = notesSnapshot.docs.filter(
        (doc) => !doc.data().isPickedUp
      ).length;
      const unpickedPhotosCount = photosSnapshot.docs.filter(
        (doc) => !doc.data().isPickedUp
      ).length;

      // console.log(
      //   "IncomingDataButton: Badge count update -",
      //   unpickedNotesCount + unpickedPhotosCount
      // );
      setTotalCount(unpickedNotesCount + unpickedPhotosCount);
    } catch (error) {
      console.error("Error checking for new data:", error);
    }
  };

  const fetchIncomingData = async () => {
    if (!flowId || !auth.currentUser) {
      console.log("IncomingDataButton: Missing flowId or user", {
        flowId,
        userEmail: auth.currentUser?.email,
      });
      return;
    }

    // console.log(
    //   "IncomingDataButton: Fetching data for flowId:",
    //   flowId,
    //   "userEmail:",
    //   auth.currentUser.email
    // );
    setLoading(true);
    try {
      // Fetch notes and photos for this flow and user
      const notesQuery = query(
        collection(db, "notes"),
        where("flowId", "==", flowId),
        where("userEmail", "==", auth.currentUser.email)
      );

      const photosQuery = query(
        collection(db, "photos"),
        where("flowId", "==", flowId),
        where("userEmail", "==", auth.currentUser.email)
      );

      const [notesSnapshot, photosSnapshot] = await Promise.all([
        getDocs(notesQuery),
        getDocs(photosQuery),
      ]);

      // console.log(
      //   "IncomingDataButton: Raw results - notes:",
      //   notesSnapshot.size,
      //   "photos:",
      //   photosSnapshot.size
      // );

      // Filter to only include items that haven't been picked up
      const fetchedNotes: IncomingNote[] = notesSnapshot.docs
        .filter((doc) => !doc.data().isPickedUp)
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as IncomingNote[];

      const fetchedPhotos: IncomingPhoto[] = photosSnapshot.docs
        .filter((doc) => !doc.data().isPickedUp)
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as IncomingPhoto[];

      // console.log(
      //   "IncomingDataButton: Filtered unpicked - notes:",
      //   fetchedNotes.length,
      //   "photos:",
      //   fetchedPhotos.length
      // );
      // console.log("IncomingDataButton: Notes data:", fetchedNotes);
      // console.log("IncomingDataButton: Photos data:", fetchedPhotos);

      setNotes(fetchedNotes);
      setPhotos(fetchedPhotos);
      setTotalCount(fetchedNotes.length + fetchedPhotos.length);
    } catch (error) {
      console.error("Error fetching incoming data:", error);
    } finally {
      setLoading(false);
    }
  };

  const addNoteAsNode = async (note: IncomingNote) => {
    try {
      const noteText = note.content || "";
      let label = "";
      let nodeNotes = "";

      if (noteText.length <= 25) {
        label = noteText;
        nodeNotes = "";
      } else {
        label = noteText.substring(0, 25) + "…";
        nodeNotes = noteText;
      }

      // Find a good position for the new node
      // Use random positioning near center to avoid overlapping
      const randomOffset = Math.floor(Math.random() * 100) - 50;
      const position = {
        x: 100 + randomOffset,
        y: 100 + randomOffset,
      };

      // Create new node
      const newNodeId = nanoid();
      const newNode: Node<NodeData> = {
        id: newNodeId,
        type: "mindmap",
        position,
        positionAbsolute: position,
        data: {
          label,
          notes: nodeNotes,
          icon: "",
          object: "",
          objectType: "text",
          objectWidth: 50,
          objectHeight: 18,
          complete: false,
          status: "none",
          flagged: "none",
          color: "#000000",
          backgroundColor: "#ffffff",
          presentationOrder: "",
          priority: "",
          links: [],
          value: "0",
          values: [],
          rollupValues: [],
          customProperties: [],
          event: undefined,
          address: undefined,
          task: undefined,
          ms365Document: undefined,
        },
        width: 50,
        height: 18,
        dragHandle: ".dragHandle",
        dragging: false,
        selected: false,
        parentId: "",
        style: {
          backgroundColor: "#ffffff",
        },
      };

      // Add node to the map
      setNodes([...nodes, newNode]);
      setIsDirty(true);

      // Mark note as picked up in Firebase
      const noteRef = doc(db, "notes", note.id);
      await updateDoc(noteRef, {
        isPickedUp: Timestamp.now(),
      });

      // Remove from local state
      setNotes(notes.filter((n) => n.id !== note.id));
      setTotalCount((prev) => prev - 1);
    } catch (error) {
      console.error("Error adding note as node:", error);
    }
  };

  const addPhotoAsNode = async (photo: IncomingPhoto) => {
    try {
      setProcessingPhotoId(photo.id);
      // Process image through Microsoft Vision API for OCR
      let transcription = "";
      try {
        transcription = await getTranscript(photo.imageUrl);
      } catch (error) {
        console.error("Error processing image with Vision API:", error);
        // Continue without transcription if API fails
      }

      // Find a good position for the new node
      // Use random positioning near center to avoid overlapping
      const randomOffset = Math.floor(Math.random() * 100) - 50;
      const position = {
        x: 150 + randomOffset,
        y: 150 + randomOffset,
      };

      // Create new node with photo as image type (like pasted images)
      const objectWidth = 200;
      const objectHeight = 200;
      const newNodeId = nanoid();
      const newNode: Node<NodeData> = {
        id: newNodeId,
        type: "image",
        position,
        positionAbsolute: position,
        data: {
          label: "image",
          notes: transcription,
          icon: "",
          object: photo.imageUrl,
          objectType: "text",
          objectWidth: objectWidth,
          objectHeight: objectHeight,
          complete: false,
          status: "none",
          flagged: "none",
          color: "black",
          backgroundColor: "#ffffff",
          presentationOrder: "",
          priority: "",
          links: [],
          value: "0",
          values: [],
          rollupValues: [],
          customProperties: [],
          event: undefined,
          address: undefined,
          task: undefined,
          ms365Document: undefined,
        },
        width: objectWidth,
        height: objectHeight,
        dragHandle: ".dragHandle",
        dragging: false,
        selected: false,
        parentId: "",
        style: {
          backgroundColor: "#ffffff",
        },
      };

      // Add node to the map
      setNodes([...nodes, newNode]);
      setIsDirty(true);

      // Mark photo as picked up in Firebase
      const photoRef = doc(db, "photos", photo.id);
      await updateDoc(photoRef, {
        isPickedUp: Timestamp.now(),
      });

      // Remove from local state
      setPhotos(photos.filter((p) => p.id !== photo.id));
      setTotalCount((prev) => prev - 1);
    } catch (error) {
      console.error("Error adding photo as node:", error);
    } finally {
      setProcessingPhotoId(null);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <>
      {externalVisible === undefined && (
        <div
          style={{
            position: "relative",
            display: "inline-block",
            marginRight: "8px",
          }}
        >
          <Button
            icon="pi pi-inbox"
            onClick={() => setVisible(true)}
            className="p-button-text p-button-plain"
            style={{
              width: "2.5rem",
              height: "2.5rem",
              color: totalCount > 0 ? "#14b8a6" : "#6c757d",
              backgroundColor: "transparent",
              border: "none",
              padding: 0,
              boxShadow: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            tooltipOptions={{ position: "bottom" }}
            tooltip="Incoming data from mobile"
          />
          {totalCount > 0 && (
            <Badge
              value={totalCount}
              severity="danger"
              style={{
                position: "absolute",
                top: "-2px",
                right: "-2px",
                minWidth: "1.25rem",
                height: "1.25rem",
                lineHeight: "1.25rem",
              }}
            />
          )}
        </div>
      )}

      <Dialog
        header="Incoming Data from Mobile"
        visible={visible}
        style={{ width: "600px", maxHeight: "80vh" }}
        onHide={() => setVisible(false)}
        className="incoming-data-dialog"
      >
        <div className="incoming-data-content">
          {loading ? (
            <div className="loading-spinner">
              <i
                className="pi pi-spin pi-spinner"
                style={{ fontSize: "2em" }}
              ></i>
              <p>Loading incoming data...</p>
            </div>
          ) : (
            <>
              {notes.length === 0 && photos.length === 0 ? (
                <div className="no-data-message">
                  <i
                    className="pi pi-inbox"
                    style={{ fontSize: "3em", color: "#ccc" }}
                  ></i>
                  <p>No new notes or photos from mobile</p>
                </div>
              ) : (
                <>
                  {notes.length > 0 && (
                    <div className="data-section">
                      <h3>
                        <i className="pi pi-file-edit"></i> Notes (
                        {notes.length})
                      </h3>
                      <div className="data-list">
                        {notes.map((note) => (
                          <div key={note.id} className="data-item">
                            <div className="data-item-content">
                              <div className="data-item-text">
                                {note.content}
                              </div>
                              <div className="data-item-meta">
                                {formatDate(note.createdAt)}
                              </div>
                            </div>
                            <Button
                              label="Add"
                              icon="pi pi-plus"
                              className="p-button-sm p-button-success"
                              onClick={() => addNoteAsNode(note)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {photos.length > 0 && (
                    <div className="data-section">
                      <h3>
                        <i className="pi pi-image"></i> Photos ({photos.length})
                      </h3>
                      <div className="data-list">
                        {photos.map((photo) => (
                          <div key={photo.id} className="data-item">
                            <div className="data-item-content">
                              <img
                                src={photo.imageUrl}
                                alt="Mobile photo"
                                className="photo-thumbnail"
                              />
                              <div className="data-item-meta">
                                {formatDate(photo.createdAt)}
                              </div>
                            </div>
                            <Button
                              label={
                                processingPhotoId === photo.id
                                  ? "Processing..."
                                  : "Add"
                              }
                              icon={
                                processingPhotoId === photo.id
                                  ? "pi pi-spin pi-spinner"
                                  : "pi pi-plus"
                              }
                              className="p-button-sm p-button-success"
                              onClick={() => addPhotoAsNode(photo)}
                              disabled={processingPhotoId === photo.id}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default IncomingDataButton;
